import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Package, ArrowRight, PartyPopper, Gift, Truck, CreditCard, MapPin, Phone, Mail, Search, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import Confetti from '../components/Confetti';
import { getPriceData } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const paymentLabels = {
    cod: '💵 Наложен платеж',
    card: '💳 Карта (MyPOS)',
    bank_transfer: '🏦 Банков превод'
};

const paymentStatusLabels = {
    pending: 'Очаква плащане',
    paid: 'Плащането е успешно',
    cod: 'Плащане при доставка',
    failed: 'Плащането е неуспешно',
    awaiting_transfer: 'Очаква банков превод',
    refunded: 'Възстановена сума'
};

const statusLabels = {
    pending: 'Поръчката е приета',
    pending_payment: 'Очаква плащане',
    awaiting_prepayment: 'Очаква предварително плащане',
    confirmed: 'Платена — потвърдена',
    processing: 'Обработва се',
    shipped: 'Изпратена',
    delivered: 'Доставена',
    cancelled: 'Отказана',
    refunded: 'Върната'
};

const OrderSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { token, user } = useAuth();
    const { fetchCart } = useCart();
    const [paymentStatus, setPaymentStatus] = useState('checking');
    const [order, setOrder] = useState(location.state?.order || null);
    const [showConfetti, setShowConfetti] = useState(false);

    const orderId = searchParams.get('order_id');
    const sessionId = searchParams.get('session_id');
    const myposSuccess = searchParams.get('mypos') === 'success';
    const isBankTransfer = searchParams.get('bank') === 'true';

    const fetchOrderSummary = useCallback(async () => {
        if (!orderId) return;
        try {
            const accessToken = sessionStorage.getItem(`order_access_${orderId}`);
            if (accessToken) {
                const res = await axios.get(`${API}/orders/${orderId}/public-summary`, {
                    params: { token: accessToken }
                });
                setOrder(res.data);
            }
        } catch (err) {
            console.error('Error fetching order summary:', err);
        }
    }, [orderId]);

    useEffect(() => {
        if (!order) fetchOrderSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refresh cart from server once card payment is confirmed (backend clears it after MyPOS confirmation)
    useEffect(() => {
        if (paymentStatus === 'paid') {
            fetchCart(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentStatus]);

    useEffect(() => {
        const checkPayment = async () => {
            if (myposSuccess && orderId) {
                let attempts = 0;
                const maxAttempts = 10;
                const poll = async () => {
                    try {
                        const response = await axios.get(`${API}/mypos/status/${orderId}`);
                        if (response.data.payment_status === 'paid') {
                            setPaymentStatus('paid');
                            setShowConfetti(true);
                            fetchOrderSummary();
                            return;
                        } else if (response.data.payment_status === 'failed') {
                            setPaymentStatus('failed');
                            return;
                        }
                        attempts++;
                        if (attempts < maxAttempts) {
                            setTimeout(poll, 2000);
                        } else {
                            setPaymentStatus('paid');
                            setShowConfetti(true);
                            fetchOrderSummary();
                        }
                    } catch (error) {
                        setPaymentStatus('paid');
                        setShowConfetti(true);
                        fetchOrderSummary();
                    }
                };
                poll();
            } else if (sessionId) {
                let attempts = 0;
                const poll = async () => {
                    try {
                        const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        if (response.data.payment_status === 'paid') {
                            setPaymentStatus('paid');
                            setShowConfetti(true);
                            return;
                        } else if (response.data.status === 'expired') {
                            setPaymentStatus('failed');
                            return;
                        }
                        attempts++;
                        if (attempts < 5) setTimeout(poll, 2000);
                        else { setPaymentStatus('pending'); setShowConfetti(true); }
                    } catch (error) {
                        setPaymentStatus('error');
                    }
                };
                poll();
            } else if (isBankTransfer && orderId) {
                setPaymentStatus('bank');
                setShowConfetti(true);
            } else if (orderId) {
                setPaymentStatus('cod');
                setShowConfetti(true);
            }
        };
        checkPayment();
    }, [sessionId, orderId, token, myposSuccess, isBankTransfer, fetchOrderSummary]);

    const shipping = order?.shipping_address || order?.address || {};
    const trackContact = order?.user_email || shipping.phone || '';
    const trackUrl = `/track/${orderId}${trackContact ? `?contact=${encodeURIComponent(trackContact)}` : ''}`;

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('bg-BG', {
            day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    const deliveryLabel = order?.delivery_method === 'sofia_driver'
        ? '🚗 Доставка с наш шофьор (София)'
        : order?.econt_delivery?.method === 'office'
        ? `📦 Еконт — до офис${order?.econt_delivery?.office_name ? `: ${order.econt_delivery.office_name}` : ''}`
        : '📦 Еконт — до адрес';

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12 px-4">
            <Confetti isActive={showConfetti} duration={4000} />

            <div className="max-w-2xl mx-auto">
                {paymentStatus === 'checking' ? (
                    <div className="bg-white p-12 rounded-3xl shadow-xl text-center">
                        <div className="w-20 h-20 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
                        <p className="text-slate-500 text-lg">Проверка на плащането...</p>
                    </div>
                ) : paymentStatus === 'failed' || paymentStatus === 'error' ? (
                    <div className="bg-white p-12 rounded-3xl shadow-xl text-center">
                        <div className="w-24 h-24 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                            <Package className="h-12 w-12 text-red-500" />
                        </div>
                        <h1 className="font-serif text-3xl font-bold mb-4 text-slate-800">Грешка при плащане</h1>
                        <p className="text-slate-500 mb-8">Възникна проблем с плащането. Моля, опитайте отново.</p>
                        <Link to="/cart">
                            <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-6 text-lg rounded-xl">
                                Обратно към количката
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Success Header */}
                        <div className="bg-white p-10 rounded-3xl shadow-2xl border border-amber-100 relative overflow-hidden text-center">
                            <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-amber-200 to-orange-200 rounded-full -translate-x-16 -translate-y-16 opacity-50" />
                            <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-br from-green-200 to-emerald-200 rounded-full translate-x-20 translate-y-20 opacity-50" />
                            <div className="relative z-10">
                                <div className="relative w-24 h-24 mx-auto mb-6">
                                    <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full animate-ping opacity-25" />
                                    <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-xl">
                                        <CheckCircle className="h-12 w-12 text-white" />
                                    </div>
                                </div>
                                <div className="flex justify-center gap-4 mb-4">
                                    <PartyPopper className="h-7 w-7 text-amber-500 animate-bounce" style={{ animationDelay: '0s' }} />
                                    <Gift className="h-7 w-7 text-orange-500 animate-bounce" style={{ animationDelay: '0.2s' }} />
                                    <PartyPopper className="h-7 w-7 text-amber-500 animate-bounce" style={{ animationDelay: '0.4s' }} />
                                </div>
                                <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-3 text-slate-800" data-testid="order-success-title">
                                    {paymentStatus === 'paid' ? 'Плащането е успешно!' : 'Поръчката е приета!'}
                                </h1>
                                <p className="text-slate-500 mb-4">
                                    {paymentStatus === 'cod'
                                        ? order?.user_email
                                            ? 'Изпратихме ви потвърждение по имейл. Плащането ще бъде при доставката.'
                                            : 'Плащането ще бъде при доставката. Запазете номера на поръчката, за да я проследявате.'
                                        : paymentStatus === 'bank'
                                        ? 'Моля, направете банков превод по посочената сметка. Ще получите потвърждение след получаване на плащането.'
                                        : order?.user_email
                                        ? 'Изпратихме ви потвърждение по имейл.'
                                        : 'Запазете номера на поръчката, за да я проследявате.'}
                                </p>
                                {orderId && (
                                    <div className="bg-amber-50 px-6 py-3 rounded-2xl inline-block" data-testid="order-success-number">
                                        <p className="text-sm text-amber-600 font-medium">Номер на поръчка</p>
                                        <p className="text-xl font-bold text-amber-700">#{orderId}</p>
                                    </div>
                                )}
                                {order?.status && (
                                    <p className="mt-3 text-sm" data-testid="order-success-status">
                                        <span className="text-slate-500">Текущ статус: </span>
                                        <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full font-medium">
                                            {statusLabels[order.status] || order.status}
                                        </span>
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Order Details */}
                        {order && (
                            <div className="bg-white rounded-3xl shadow-lg border border-amber-100 p-8" data-testid="order-success-details">
                                <h2 className="font-serif text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                                    <Package className="w-5 h-5 text-amber-500" /> Детайли на поръчката
                                </h2>

                                {/* Items */}
                                <div className="space-y-3 mb-5">
                                    {(order.items || []).map((item, idx) => (
                                        <div key={idx} className="flex items-center gap-3 py-2 border-b border-amber-50 last:border-0">
                                            {item.image_url && (
                                                <img src={item.image_url} alt={item.name} className="w-14 h-14 object-cover rounded-lg flex-shrink-0" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-slate-800 text-sm">{item.name}</p>
                                                <p className="text-xs text-slate-500">{item.quantity} x {getPriceData(item.price || 0).eurFormatted} €</p>
                                            </div>
                                            <p className="font-semibold text-sm text-slate-800">
                                                {getPriceData((item.price || 0) * (item.quantity || 1)).eurFormatted} €
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                {/* Totals */}
                                <div className="border-t border-amber-100 pt-4 space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Продукти</span>
                                        <span>{getPriceData(order.subtotal ?? ((order.total || 0) - (order.shipping_cost || 0))).eurFormatted} €</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Доставка</span>
                                        <span className={order.shipping_cost > 0 ? '' : 'text-green-600 font-medium'}>
                                            {order.shipping_cost > 0 ? `${getPriceData(order.shipping_cost).eurFormatted} €` : 'Безплатна'}
                                        </span>
                                    </div>
                                    {order.discount > 0 && (
                                        <div className="flex justify-between text-green-600">
                                            <span>Отстъпка</span>
                                            <span>-{getPriceData(order.discount).eurFormatted} €</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-lg font-bold pt-2 border-t border-amber-100">
                                        <span>Крайна сума</span>
                                        <div className="text-right">
                                            <span className="text-amber-600" data-testid="order-success-total">{getPriceData(order.total || 0).eurFormatted} €</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Payment & Delivery */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                                    <div className="bg-amber-50/60 rounded-xl p-4">
                                        <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> Начин на плащане</p>
                                        <p className="font-medium text-sm text-slate-800" data-testid="order-success-payment">{paymentLabels[order.payment_method] || order.payment_method}</p>
                                        <p className="text-xs mt-1 text-slate-600">
                                            Статус: <span className={`font-medium ${order.payment_status === 'paid' ? 'text-green-600' : order.payment_status === 'failed' ? 'text-red-600' : 'text-amber-600'}`}>
                                                {paymentStatusLabels[order.payment_status] || order.payment_status}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="bg-amber-50/60 rounded-xl p-4">
                                        <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Доставка</p>
                                        <p className="font-medium text-sm text-slate-800" data-testid="order-success-delivery">{deliveryLabel}</p>
                                        <p className="text-xs mt-1 text-slate-600 flex items-start gap-1">
                                            <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                            {[shipping.address, shipping.city, shipping.postal_code].filter(Boolean).join(', ') || '—'}
                                        </p>
                                    </div>
                                </div>

                                {/* Customer Info */}
                                <div className="mt-4 bg-slate-50 rounded-xl p-4 text-sm space-y-1" data-testid="order-success-customer">
                                    <p className="flex items-center gap-2 text-slate-700"><User className="w-3.5 h-3.5 text-slate-400" /> {shipping.name}</p>
                                    <p className="flex items-center gap-2 text-slate-700"><Phone className="w-3.5 h-3.5 text-slate-400" /> {shipping.phone}</p>
                                    {order.user_email && (
                                        <p className="flex items-center gap-2 text-slate-700"><Mail className="w-3.5 h-3.5 text-slate-400" /> {order.user_email}</p>
                                    )}
                                    {order.created_at && (
                                        <p className="text-xs text-slate-400 pt-1">Поръчано на: {formatDate(order.created_at)}</p>
                                    )}
                                </div>

                                {/* Next steps */}
                                <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800">
                                    <p className="font-medium mb-1">Какво следва?</p>
                                    <p className="text-xs leading-relaxed">
                                        {order.payment_method === 'bank_transfer'
                                            ? 'Очакваме вашия банков превод. След получаване на плащането ще обработим и изпратим поръчката.'
                                            : order.delivery_method === 'sofia_driver'
                                            ? 'Ще обработим поръчката и нашият шофьор ще ви достави в рамките на София. Ще се свържем с вас по телефона.'
                                            : 'Ще обработим поръчката и ще я изпратим с Еконт. Ще получите номер за проследяване, когато пратката бъде създадена.'}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to={trackUrl} data-testid="order-success-track-btn">
                                <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg w-full sm:w-auto">
                                    <Search className="mr-2 h-5 w-5" />
                                    Проследи поръчката
                                </Button>
                            </Link>
                            {user && (
                                <Link to="/orders" data-testid="order-success-my-orders-btn">
                                    <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 px-8 py-6 text-lg rounded-xl w-full sm:w-auto">
                                        Моите поръчки
                                        <ArrowRight className="ml-2 h-5 w-5" />
                                    </Button>
                                </Link>
                            )}
                            <Link to="/products" data-testid="order-success-continue-btn">
                                <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 px-8 py-6 text-lg rounded-xl w-full sm:w-auto">
                                    Продължи пазаруването
                                </Button>
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderSuccessPage;
